"use client"

import { AnimatePresence, animate, motion, useMotionValue } from "motion/react"
import type { CSSProperties, Ref } from "react"
import { useEffect, useMemo, useRef, useState } from "react"

import { ContentPlayer } from "@/components/player/content-player"
import { Button } from "@/components/ui/button"
import { Carousel } from "@/components/ui/carousel"
import { staticCanonicalUrls } from "@/constants/static-canonical-urls"
import { useIsMobile } from "@/hooks/use-mobile"
import type { PlayableItem, PlayableKind } from "@/lib/player/playable-item"
import { cn, focalPointStyle } from "@/lib/utils"
import type { Playlist } from "@/types"

import { ArticleReader } from "./jacket/article-reader"
import { AudioWell } from "./jacket/audio-well"
import {
  COVER_INTENSITY,
  type Cover,
  seedFor,
  selectCover,
} from "./jacket/cover-art"
import { PatternField } from "./jacket/cover-patterns"
import { Disc, DiscArt } from "./jacket/disc"
import { splitTitle } from "./jacket/display-title"
import "./jacket/feed.css"
import {
  BylineBlock,
  CatalogMark,
  DUOTONE_FILTER,
  DotScreen,
  DuotoneDefs,
  EndOfStackCap,
  FOCUS_RING,
  GrainDefs,
  GrainLayer,
  HairlineRule,
  PeekHint,
  PlaylistMark,
  TopChrome,
} from "./jacket/jacket-chrome"
import { JacketFonts } from "./jacket/jacket-fonts"
import { JacketNotes } from "./jacket/jacket-notes"
import { LinerCard, SpineShadow } from "./jacket/liner-card"
import { EASE_PAPER, PAPER, REDUCED_FADE, VINYL_RPM_SEC } from "./jacket/motion"
import { PaperOutro } from "./jacket/paper-outro"
import type { OutroSlate } from "./jacket/paper-types"
import { firstSentence } from "./jacket/strip-html"
import {
  SERIF,
  chromeLabel,
  kicker,
  linerNotes,
  marginalia,
} from "./jacket/type-voices"
import { UpNextCaption, UpNextRule } from "./jacket/up-next"
import { useAmbientLive, useAmbientLoop } from "./jacket/use-ambient-loop"
import type {
  FeedShellProps,
  JacketEntry,
  JacketPhase,
} from "./jacket/use-jacket-feed"
import {
  JACKET_SLIDE_CLASS,
  buildJacketQueue,
  jacketEmblaOpts,
  liveMutedProp,
  useJacketFeed,
} from "./jacket/use-jacket-feed"
import { useMediaTravel } from "./jacket/use-media-travel"
import { useVinylPlayback } from "./jacket/use-vinyl-playback"

/**
 * CANDIDATE B — THE RECORD SLEEVE (BRIEF v2 §6.B, recomposed by BRIEF v4;
 * die-cut retired for the slipped disc)
 *
 * The jacket metaphor made literal: every plate is a TRUE-SQUARE Paper
 * White sleeve on the Oat ground with a true-vinyl record (Ø 0.9 of the
 * side) WEDGED inside it, slipped part-way out of the right edge — one
 * grammar on both platforms. No mask does this: the sleeve paints ABOVE
 * the media layers, so the jacket itself occludes the seated portion of
 * the disc and the exposed crescent is simply what clears its edge; the
 * back-panel lip below the disc stack and the mouth shadow on the
 * crescent complete the paper sandwich. The card face is therefore
 * whole, and the liner notes get the full column the die-cut used to
 * eat. The threshold draws the sleeve out sideways (a record is pulled
 * left, not down) while the media-box TRAVELS from its seat to its rect
 * on the framed stage on springMediaZ, irising open from disc to frame
 * as it flies (use-media-travel). The sleeve parks as a compressed spine
 * along the left viewport edge, which is also this candidate's escape
 * dock, with the crate riffle beneath it; close is the reverse travel,
 * and the sleeve re-enters after the record is home.
 *
 * Ambient is poster-first with one exception — §6.B's YouTube-only
 * stretch: a loop-eligible slide (bridged video, pointer-fine, no
 * saveData) runs a live-muted loop on the exposed crescent from selection,
 * and the threshold tap unmutes it IN PLACE through the bridge; the loop
 * never restarts. One iframe per SELECTION, never per slide. The view's
 * single yellow moment is the irregular highlighter stroke inside the
 * liner notes.
 */

/* The resting shadow — the mockup's navy 0 1px 4px, a deliberate override
 * of the shadow-xs house default (recorded in the shell-notes retint
 * addendum) — and the mid-flight swell the departing sleeve carries,
 * keyframed so it lands back at nothing as the card leaves the frame
 * (§3.1: flight shadows land back). Navy is lighter than the old
 * near-black, so the equal-alpha flight swell reads softer, not heavier. */
const REST_SHADOW = "0px 1px 4px rgba(31, 58, 102, 0.10)"
const FLIGHT_SHADOW = [
  REST_SHADOW,
  "0px 16px 40px rgba(31, 58, 102, 0.18)",
  "0px 0px 0px rgba(31, 58, 102, 0)",
]

/* The media-box wrapper must be the same aspect the live leaf renders (an
 * iframe can't object-cover — a wrong-aspect wrapper stretches the video).
 * Mirrors ContentPlayer's resolveAspect/DEFAULT_ASPECT — keep in sync by
 * hand; content-player.tsx is do-not-modify. */
const ASPECT_DEFAULT: Record<PlayableKind, string> = {
  video: "16/9",
  audio: "1/1",
  photo: "4/5",
  article: "4/5",
}

function aspectNumberFor(item: PlayableItem): number {
  const raw = item.aspect ?? ASPECT_DEFAULT[item.kind]
  const [w, h] = raw.split("/").map(Number)
  return w > 0 && h > 0 ? w / h : 16 / 9
}

/* The crate's side+number for a plate, uppercased for the record label
 * ("A1", "B3") — the same A/B split the parked spine draws (SpineDock),
 * printed onto the spinning disc as its rotation cue. */
function catalogMark(index: number, total: number): string {
  const half = Math.ceil(total / 2)
  const side = index < half ? "A" : "B"
  const number = index < half ? index + 1 : index - half + 1
  return `${side}${number}`
}

export function RecordSleeveShell({
  playlist,
  tracks,
  initialPosition,
  outroSlate,
  forceOutro,
}: FeedShellProps) {
  const { items, startIndex } = useMemo(
    () => buildJacketQueue(tracks, initialPosition),
    [tracks, initialPosition],
  )

  return (
    <Carousel.Root
      orientation="vertical"
      opts={jacketEmblaOpts(startIndex)}
      className="jacket-sleeve-palette bg-surface-base h-dvh w-dvw overflow-hidden"
    >
      <JacketFonts />
      <Body
        playlist={playlist}
        items={items}
        outroSlate={outroSlate}
        forceOutro={forceOutro}
      />
    </Carousel.Root>
  )
}

/* -------------------------------------------------------------------- body */

function Body({
  playlist,
  items,
  outroSlate,
  forceOutro,
}: {
  playlist: Playlist
  items: JacketEntry[]
  outroSlate?: OutroSlate | null
  forceOutro?: boolean
}) {
  const ambientLive = useAmbientLive()
  const feed = useJacketFeed({ playlist, items, ambientLive, forceOutro })
  const {
    api,
    containerRef,
    selectedIndex,
    phase,
    isOpen,
    finished,
    next,
    scrollPrev,
    isMuted,
    muteSuppressed,
    reduce,
    openThreshold,
    jacketDeparted,
    mediaLanded,
    returnToAmbient,
    sleeveComposed,
    closeSettled,
    advanceNow,
    stay,
    replay,
    leave,
    setLiveMuted,
  } = feed
  const isMobile = useIsMobile()

  // The peek carets fade out the moment the stack moves — their nudge has done
  // its job. Embla fires `scroll` on any drag/glide and `settle` when it stops.
  const [scrolling, setScrolling] = useState(false)
  useEffect(() => {
    if (!api) return
    const start = () => setScrolling(true)
    const stop = () => setScrolling(false)
    api.on("pointerDown", start)
    api.on("scroll", start)
    api.on("settle", stop)
    return () => {
      api.off("pointerDown", start)
      api.off("scroll", start)
      api.off("settle", stop)
    }
  }, [api])

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className="relative h-full w-full focus:outline-none"
    >
      <GrainDefs />
      <DuotoneDefs />

      {/* Animation for moving SleeveSlide's carousel always from viewpod. */}
      <motion.div
        className="absolute inset-0"
        initial={false}
        animate={
          finished
            ? reduce
              ? { opacity: 0, transition: REDUCED_FADE }
              : { x: "-100%", transition: { duration: 0.42, ease: EASE_PAPER } }
            : reduce
              ? { opacity: 1, transition: REDUCED_FADE }
              : { x: 0, transition: PAPER }
        }
        // Off-screen and inert once finished — PaperOutro owns the pointer.
        style={{ pointerEvents: finished ? "none" : "auto" }}
      >
        <Carousel.Content className="flex h-full flex-col">
          {items.map((entry, index) => (
            <Carousel.Item key={entry.track.id} className={JACKET_SLIDE_CLASS}>
              <SleeveSlide
                playlist={playlist}
                entry={entry}
                index={index}
                total={items.length}
                phase={index === selectedIndex ? phase : "ambient"}
                isSelected={index === selectedIndex}
                loopEligible={ambientLive(entry.item)}
                isMuted={isMuted}
                reduce={reduce}
                next={index === selectedIndex ? next : undefined}
                onOpen={openThreshold}
                onDeparted={jacketDeparted}
                onLanded={mediaLanded}
                onReturn={returnToAmbient}
                onComposed={sleeveComposed}
                onCloseSettled={closeSettled}
                onAdvance={advanceNow}
                onStay={stay}
              />
            </Carousel.Item>
          ))}
        </Carousel.Content>
      </motion.div>

      <CrateRail
        total={items.length}
        selectedIndex={selectedIndex}
        hidden={isOpen}
      />
      <PlaylistMark
        title={playlist.title}
        curator={playlist.user?.name}
        hidden={isOpen}
      />
      <TopChrome
        isMuted={isMuted}
        muteSuppressed={muteSuppressed}
        onToggleMute={() => setLiveMuted(!isMuted)}
        onClose={leave}
      />
      {/* A single restrained caret in the peek void marks that the stack
          scrolls up/down and doubles as a tap-to-turn target — no faux sleeve
          edge. The crate's end caps the void under the final plate, so the
          bottom caret yields to it there. Both hide while a plate is open. */}
      {!isOpen && selectedIndex > 0 && (
        <PeekHint
          edge="top"
          label={items[selectedIndex - 1].item.title}
          onActivate={scrollPrev}
          dimmed={scrolling}
          reduce={reduce}
        />
      )}
      {!isOpen && selectedIndex < items.length - 1 && (
        <PeekHint
          edge="bottom"
          label={items[selectedIndex + 1].item.title}
          onActivate={advanceNow}
          dimmed={scrolling}
          reduce={reduce}
        />
      )}
      <EndOfStackCap
        visible={selectedIndex === items.length - 1}
        onActivate={advanceNow}
      />

      <AnimatePresence>
        {finished && (
          <PaperOutro
            key="paper-outro"
            playlistTitle={playlist.title}
            creator={playlist.user?.name}
            onReplay={replay}
            onClose={leave}
            reduce={reduce}
            wide={!isMobile}
            // The server-resolved onward slate. When a playlist has validated
            // placements it drives the outro; the Being Herd cta below is the
            // empty-slate fallback.
            slate={outroSlate}
            cta={
              <Button
                data-outro-primary
                type="button"
                color="primary"
                size="lg"
                onClick={() =>
                  window.open(staticCanonicalUrls.SIGN_UP_BEING_HERD, "_blank")
                }
                className="w-full rounded-xs md:w-xs"
              >
                Sign up for Being Herd
              </Button>
            }
          />
        )}
      </AnimatePresence>
    </div>
  )
}

/* ------------------------------------------------------------------- slide */

interface SleeveSlideProps {
  playlist: Playlist
  entry: JacketEntry
  index: number
  total: number
  phase: JacketPhase
  isSelected: boolean
  /** ambientLive(item) — may this slide loop live behind the die-cut? */
  loopEligible: boolean
  isMuted: boolean
  reduce: boolean
  next?: JacketEntry
  onOpen: () => void
  onDeparted: () => void
  onLanded: () => void
  onReturn: () => void
  /** Beat 1 done — the empty sleeve has crossfaded in composed. */
  onComposed: () => void
  onCloseSettled: () => void
  onAdvance: () => void
  onStay: () => void
}

function SleeveSlide({
  playlist,
  entry,
  index,
  total,
  phase,
  isSelected,
  loopEligible,
  isMuted,
  reduce,
  next,
  onOpen,
  onDeparted,
  onLanded,
  onReturn,
  onComposed,
  onCloseSettled,
  onAdvance,
  onStay,
}: SleeveSlideProps) {
  const { item } = entry
  // The seeded cover for this record — drives the printed sleeve AND tints
  // the elevated paper (below), so the whole record reads as one colourway.
  const cover = selectCover(seedFor(item))
  const isOpen = phase !== "ambient"
  const { looping } = useAmbientLoop({
    item,
    isSelected,
    isOpen,
    eligible: loopEligible,
  })

  // Media Z (v4 §7): kinds with a media rect ride the disc-fitted media-box
  // out of the porthole to the stage; article/audio swap sleeve-for-stage on
  // paper with no travel. The box's ambient rest is pure CSS
  // (.jacket-media-box, from the --hole-* vars) — no seat effect, no
  // breakpoint listener; use-media-travel pins px only while a travel runs.
  const travels = item.kind === "video" || item.kind === "photo"
  const aspectNum = aspectNumberFor(item)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const mediaBoxRef = useRef<HTMLDivElement | null>(null)
  const holeProbeRef = useRef<HTMLDivElement | null>(null)
  const portholeTextureRef = useRef<HTMLDivElement | null>(null)
  const stageRef = useRef<HTMLDivElement | null>(null)
  const stageMediaRef = useRef<HTMLDivElement | null>(null)

  // Phases where the media is OUT on the stage (the sleeve is docked). Beat 1
  // ("closing") and Beat 2 ("seating") are excluded: the sleeve is present and
  // the record is either lifted off or sliding back in.
  const staged = phase === "opening" || phase === "active" || phase === "upnext"

  // The open audio stage's record spins only while sound actually runs — read
  // from the live bridge (YouTube or Spotify) off this slide's root. Self-gates
  // to audio kinds; every other kind gets a steady `false`.
  const { playing: audioPlaying } = useVinylPlayback({
    rootRef,
    item,
    staged,
  })

  useMediaTravel({
    phase,
    reduce,
    travels,
    rootRef,
    mediaBoxRef,
    holeProbeRef,
    portholeTextureRef,
    stageRef,
    stageMediaRef,
    onLanded,
    onCloseSettled,
  })

  return (
    // Slipped-disc geometry, ONE grammar on both platforms (square-sleeve
    // pass): the card is a TRUE SQUARE (--card-h = --card-w) and the
    // record — r = 0.45·w, a true-vinyl Ø of 0.9 of the side (the
    // content-hugged card and the half-out disc read as a fortune
    // cookie and were rejected) — sits INSIDE the sleeve, its center
    // pulled 0.11·w short of the right edge, so a long gentle crescent
    // slips out past it at the card's vertical middle (≡ canvas midline
    // — one consistent position on every slide). The card rect is
    // DECLARED once here (v4 §8) — --card-x/y/w/h — and --hole-x/y/r
    // derive from it, so the card, peek sliver, wedge layers and every
    // porthole face move together.
    // COUPLED LITERALS: the phone width divisor /1.11 folds the crescent
    // (--disc-out = 0.11·w) into the width bound, so the ≥12px viewport
    // clearance holds by construction — tune the crescent fraction only
    // in lockstep with that divisor (band 0.10–0.12). Desktop needs no
    // divisor: the width clamp's own terms keep card-right + crescent
    // clear at every md width (worst case 768×1024 → ~50px). The disc is
    // inscribed with 0.05·w clearance top and bottom by construction —
    // the old 0.46·card-h tangency guard and the clearance min() inside
    // --hole-r are gone with the content-hug apparatus
    // (--card-h-measured, --card-b, --disc-reserve, use-card-height).
    // The height terms (100dvh − 158px / − 128px) sit OUTSIDE the
    // desktop clamp so its 430px floor can never defeat them on a short
    // window. NO % may enter the --card-w → --card-h → --hole-r chain —
    // the registered <length> @property rejects it and silently falls
    // back to 88px forever; --card-y stays unregistered so % may ride it
    // (its consumers resolve at use sites / through the probe).
    <div
      ref={rootRef}
      data-component="sleeve-slide"
      className="absolute inset-0 [--card-h:var(--card-w)] [--card-w:min(calc((100vw_-_24px)/1.11),calc(100dvh_-_158px))] [--card-x:12px] [--card-y:calc((100%_-_var(--card-h))/2)] [--disc-out:calc(var(--card-w)*0.11)] [--hole-r:calc(var(--card-w)*0.45)] [--hole-x:calc(var(--card-x)_+_var(--card-w)_-_var(--hole-r)_+_var(--disc-out))] [--hole-y:calc(var(--card-y)_+_var(--card-h)/2)] [--mouth-w:16px] md:[--card-w:min(clamp(430px,min(0.35*min(100vw,100vh)_+_320px,0.82*min(100vw,100vh)),860px),calc(100dvh_-_128px))] md:[--card-x:clamp(64px,3vw_+_24px,112px)] md:[--mouth-w:24px]"
      // The elevated paper across this whole record — the sleeve lip, the
      // liner credit, the open stage panels — borrows the cover's own pale
      // glow, so the record reads as one colourway rather than white paper
      // wearing a coloured print. Scoped here (not the global token); softened
      // toward white so Ink text stays AA on the tint.
      style={
        {
          "--media-aspect": aspectNum,
          "--color-surface-elevated": `color-mix(in srgb, ${cover.glow} 72%, white)`,
        } as CSSProperties
      }
    >
      {/* Hole probe: a 0×0 marker at the disc center. The travel's rest-rect
          math reads it (percentages inside --hole-x/y resolve only at a use
          site), and §9.3's measurements borrow the same trick. */}
      <div
        ref={holeProbeRef}
        aria-hidden="true"
        className="absolute h-0 w-0"
        style={{ left: "var(--hole-x)", top: "var(--hole-y)" }}
      />

      {/* BACK-PANEL LIP — the bottom of the whole disc stack: the sliver
          of the sleeve's BACK panel visible past the front panel's edge,
          so the record is seen to sit BETWEEN two sheets of paper. Shares
          PortholeBase's exact timing (below): on close the back panel is
          already there waiting with the ghost floor, the record lands on
          it, and the sleeve slides over it — the sandwich reassembles in
          physical order. Present through BOTH close beats — the record seats
          back onto it — so it un-fades the moment the media leaves the stage
          (staged only). */}
      <SleeveLip faded={staged} />

      {/* PORTHOLE FLOOR — the poster's disc-fitted focal crop (v4 §6), the
          permanent face under everything: what ambient shows for
          poster-first slides, and what floors the iframe's black boot.
          Faded for the whole open state (the theater/label gap would
          otherwise expose a slice of it at its rest position); it reveals only
          at rest, under the seated record. */}
      <PortholeBase item={item} faded={phase !== "ambient"} />

      {/* THE STAGE — mounts under the media-box (the record lands ON the
          platter); article/audio ride it as their whole open state. */}
      <AnimatePresence>
        {staged && (
          <SleeveStage
            key="stage"
            ref={stageRef}
            playlist={playlist}
            entry={entry}
            cover={cover}
            index={index}
            total={total}
            phase={phase}
            reduce={reduce}
            travels={travels}
            audioPlaying={audioPlaying}
            stageMediaRef={stageMediaRef}
            onSettled={onLanded}
          />
        )}
      </AnimatePresence>

      {/* MEDIA BOX — the ONE travelling slot (video + photo). Lives for the
          slide's whole life; .jacket-media-box owns its ambient rest (disc-
          fitted, true-aspect, 8px bleed) and use-media-travel pins px over
          it in flight. Video keeps ONE stable mount serving both the
          ambient loop and open playback: for a live loop the condition
          never toggles across the threshold (ambientEnded can't latch while
          open), so the same iframe survives the tap and the unmute happens
          in place. */}
      {travels && (
        <div
          ref={mediaBoxRef}
          data-component="media-box"
          // The exposed crescent sits OUTSIDE the threshold button, so in
          // ambient (and on the way home) the box must be deaf — a tap on
          // the crescent would otherwise hit the raw embed (YouTube pauses,
          // chrome appears) and eat Embla's drags. Open playback needs the
          // events back: the stage video's controls ARE the iframe.
          className={cn(
            "jacket-media-box",
            (!isOpen || phase === "closing" || phase === "seating") &&
              "pointer-events-none",
          )}
        >
          {item.kind === "video" && isOpen && !looping && item.poster?.src && (
            // Fresh-mount boot floor: rides INSIDE the box so black boot
            // frames stay covered mid-flight (§7). Never mounted in ambient
            // — the porthole faces keep their crop parity.
            <img
              src={item.poster.src}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover"
              style={focalPointStyle(item.poster.focal_point)}
            />
          )}
          {item.kind === "video" && (looping || isOpen) && (
            // YT iframes boot black — hold the poster beneath, then a 200ms
            // crossfade in (C's reveal pattern). Covers the loop mounting at
            // selection, threshold fresh-mounts, and spent-loop reopens.
            // Reduced motion: the fade IS the named fallback (opacity only).
            <motion.div
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { delay: 0.3, duration: 0.2 },
              }}
            >
              <ContentPlayer
                item={item}
                mode="live"
                active={isSelected}
                muted={liveMutedProp(item, isMuted)}
                className="aspect-auto h-full max-h-none w-full rounded-none"
              />
            </motion.div>
          )}
          {item.kind === "photo" && isOpen && (
            // LivePhoto's 6s timer IS the Up Next trigger — mounted only
            // past the threshold, never in ambient; it rides the box to the
            // stage. Only video carries the never-remount law.
            <div className="absolute inset-0">
              <ContentPlayer
                item={item}
                mode="live"
                active
                className="aspect-auto h-full max-h-none w-full rounded-none"
              />
            </div>
          )}
        </div>
      )}

      {/* A live loop yields the duotoned poster to the unfiltered motion —
          the dot screen and grain persist above it (C's color-pass story in
          miniature, inside the porthole). Wrapped in a transform carrier: on
          Beat 2 use-media-travel writes translateX here so the vinyl rides the
          media-box in lockstep (the wrapper holds no iframe, so a transform is
          legal); PortholeTexture keeps owning its own opacity. Visible/sliding
          during "seating" and at rest; off through open + Beat 1. */}
      <div
        ref={portholeTextureRef}
        className="pointer-events-none absolute inset-0"
      >
        <PortholeTexture faded={phase !== "ambient" && phase !== "seating"} />
      </div>

      {/* THE RECORD IS AN AFFORDANCE — a circular tap target sat on the disc.
          It paints UNDER the sleeve (which occludes the seated half), so only
          the exposed crescent takes the pointer; tapping the vinyl slips it
          out exactly as tapping the face does. Ambient/selected only. Embla
          still owns a drag that starts here (watchDrag doesn't exclude it),
          the same contract the face threshold keeps. A distinct label keeps
          it off the "Open —" selector the face threshold owns. */}
      {isSelected && phase === "ambient" && (
        <button
          type="button"
          data-component="disc-open"
          onClick={onOpen}
          aria-label={`Play the record — ${item.title}`}
          className={cn("absolute cursor-pointer rounded-full", FOCUS_RING)}
          style={{
            left: "calc(var(--hole-x) - var(--hole-r))",
            top: "calc(var(--hole-y) - var(--hole-r))",
            width: "calc(var(--hole-r) * 2)",
            height: "calc(var(--hole-r) * 2)",
          }}
        />
      )}

      {/* SLEEVE ⇄ PARKED SPINE. Two discrete elements (a layoutId morph
          would distort the printed type mid-flight): the sleeve exits left
          — a record drawn out sideways — and the spine takes the left edge
          as the escape dock, the crate riffle beneath it. The empty sleeve
          returns at "closing" (Beat 1, crossfade) and stays through "seating"
          (Beat 2) as the record slides back into it. */}
      <AnimatePresence initial={false} onExitComplete={onDeparted}>
        {phase === "ambient" || phase === "closing" || phase === "seating" ? (
          <SleeveCard
            key="jacket"
            playlist={playlist}
            entry={entry}
            cover={cover}
            index={index}
            total={total}
            phase={phase}
            reduce={reduce}
            onOpen={onOpen}
            onComposed={onComposed}
          />
        ) : (
          <SpineDock
            key="dock"
            entry={entry}
            index={index}
            total={total}
            next={next}
            reduce={reduce}
            onReturn={onReturn}
            onAdvance={onAdvance}
          />
        )}
      </AnimatePresence>

      {/* UP NEXT — §7.3, in sleeve material, on the Oat ground band the
          stage reserves (never intersecting it). */}
      <AnimatePresence>
        {phase === "upnext" && next && (
          <SleeveUpNext
            key="upnext"
            next={next}
            nextIndex={index + 1}
            total={total}
            reduce={reduce}
            onAdvance={onAdvance}
            onStay={onStay}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

/* --------------------------------------------------------------- backdrop */

/**
 * The porthole's vinyl read (designer handoff — "Vinyl Covers"): the refined
 * SVG record (Disc) at ~70% over whatever media the floor / media-box show, so
 * the crescent past the sleeve edge reads as the record with the media as a
 * nuanced detail in it — not a full-colour poster poking out. The dot screen
 * and 2.5% grain composite ABOVE the disc (overlay layers legally sit over
 * iframes; SVG filters never apply to them), and the MOUTH SHADOW rides
 * topmost. Fades as one unit at the threshold; the stage has its own frame
 * grammar, so the media arrives in full colour only once it travels out.
 */
function PortholeTexture({ faded }: { faded: boolean }) {
  return (
    <motion.div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        clipPath: "circle(var(--hole-r) at var(--hole-x) var(--hole-y))",
      }}
      initial={false}
      animate={{ opacity: faded ? 0 : 1 }}
      // Reduced motion: the 200ms crossfade is itself the named fallback —
      // opacity only, no movement to remove.
      transition={{ duration: 0.2 }}
    >
      {/* The record itself, at ~70% so the media beneath reads through. */}
      <Disc />
      <DotScreen />
      <GrainLayer />
      {/* The mouth shadow: the front panel's cast shadow on the emerging
          record, darkest at the card's right edge and gone by --mouth-w. The
          circle clip confines it to the disc; this inset-0 div's background
          coordinates ARE slide coordinates, so the card edge is addressable.
          The leading transparent stop keeps the darkening from extending under
          the card during the card-exit/texture-fade overlap. A shadow must die
          with the paper that casts it — so it fades with the texture. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, transparent calc(var(--card-x) + var(--card-w) - 1px), rgb(var(--batyr-ink-rgb) / 0.5) calc(var(--card-x) + var(--card-w)), rgb(var(--batyr-ink-rgb) / 0.22) calc(var(--card-x) + var(--card-w) + 8px), transparent calc(var(--card-x) + var(--card-w) + var(--mouth-w, 18px)))",
        }}
      />
    </motion.div>
  )
}

/**
 * The open audio stage's record: the SAME groove art the porthole shows,
 * blown up birds-eye behind the content card and spun at 33⅓ rpm while the
 * embed actually plays (bridge-reported — YouTube or Spotify). It freezes in
 * place on pause (the motion value simply stops), and stays static under
 * reduced motion. Decorative: deaf to the pointer, hidden from AT, clipped to
 * the stage panel so its right-top bleed reads as a record too big for the
 * frame — never a floating coaster.
 */
function SpinningRecord({
  playing,
  reduce,
  label,
  catalog,
}: {
  playing: boolean
  reduce: boolean
  /** The sleeve's colourway, printed onto the disc's centre label. */
  label: { base: string; ink: string; glow: string }
  /** The side+number mark on the label (e.g. "A1") — the rotation cue. */
  catalog: string
}) {
  const rotate = useMotionValue(0)
  useEffect(() => {
    if (reduce || !playing) return
    // Loop from the current angle by +360; 360 ≡ 0, so each repeat snaps back
    // seamlessly. Stopping leaves the value wherever it landed — the record
    // holds the groove it was on, it doesn't rewind.
    const controls = animate(rotate, rotate.get() + 360, {
      ease: "linear",
      duration: VINYL_RPM_SEC,
      repeat: Infinity,
      repeatType: "loop",
    })
    return () => controls.stop()
  }, [playing, reduce, rotate])

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute top-[-6%] right-[-6%] aspect-square w-[72%] max-w-[460px] min-w-[240px] drop-shadow-[0_10px_30px_rgba(31,58,102,0.20)]">
        <motion.div className="h-full w-full" style={{ rotate }}>
          <DiscArt
            opacity={0.96}
            label={label}
            catalog={catalog}
            spindle
            sheen
          />
        </motion.div>
      </div>
    </div>
  )
}

/**
 * The back panel's lip (square-sleeve pass): a 12px-visible strip of the
 * SAME paper as the card, 1px tucked under the front panel (no Oat
 * pinhole on subpixel rounding), running the card's FULL height — a
 * jacket's mouth runs the whole open edge; a chord-height tab would read
 * as a sticker glued behind the record. It wears shade instead of a
 * shadow (a second drop shadow would read as a double card): a hairline
 * terminator on its own right edge, the disc's contact-shadow ring
 * segment pressing on it, and a wash darkest at the seam. It sits below
 * the entire disc stack — it cannot be a child of the card, whose
 * transform makes a stacking context that would lift it above the disc.
 * Deaf to the pointer: the crescent-side surface stays Embla's.
 * Lifecycle is PortholeBase's exactly (200ms opacity; reduced motion:
 * the fade IS the named fallback) — during "closing" it stands WITH the
 * ghost floor, never alone.
 */
function SleeveLip({ faded }: { faded: boolean }) {
  return (
    <motion.div
      aria-hidden="true"
      data-component="sleeve-lip"
      className="bg-surface-elevated pointer-events-none absolute rounded-none"
      style={{
        left: "calc(var(--card-x) + var(--card-w) - 1px)",
        top: "var(--card-y)",
        height: "var(--card-h)",
        width: 13,
        backgroundImage: [
          // Right-edge terminator — the paper ends crisply against Oat.
          "linear-gradient(to left, rgb(var(--batyr-ink-rgb) / 0.16) 0, rgb(var(--batyr-ink-rgb) / 0.16) 1px, transparent 1px)",
          // The record's contact shadow — a ring segment just outside the
          // vinyl's perimeter, so the disc visibly PRESSES on the back
          // panel where it crosses it. Gradient coords are lip-local:
          // center = hole − lip origin.
          "radial-gradient(circle at calc(var(--hole-x) - var(--card-x) - var(--card-w) + 1px) calc(var(--hole-y) - var(--card-y)), transparent var(--hole-r), rgb(var(--batyr-ink-rgb) / 0.12) calc(var(--hole-r) + 1px), rgb(var(--batyr-ink-rgb) / 0.12) calc(var(--hole-r) + 3px), transparent calc(var(--hole-r) + 6px))",
          // Shade wash — darkest where it disappears under the front panel.
          "linear-gradient(to right, rgb(var(--batyr-ink-rgb) / 0.16), rgb(var(--batyr-ink-rgb) / 0.07))",
        ].join(", "),
      }}
      initial={false}
      animate={{ opacity: faded ? 0 : 1 }}
      // Reduced motion: the 200ms crossfade is itself the named fallback.
      transition={{ duration: 0.2 }}
    />
  )
}

/**
 * What the die-cut shows in ambient (and what floors the reveal): the
 * poster's disc-fitted focal crop (v4 §6) — never the card's accidental
 * bottom-right corner. Fades for every open (a landed stage must not share
 * the frame with the ghost of the disc at rest) and returns at "closing",
 * ahead of the record's landing. Reduced motion: the 200ms crossfade is
 * itself the named fallback (opacity only).
 */
function PortholeBase({ item, faded }: { item: PlayableItem; faded: boolean }) {
  return (
    <motion.div
      aria-hidden="true"
      data-component="porthole"
      className="jacket-porthole-face pointer-events-none overflow-hidden"
      style={{ clipPath: "circle(var(--hole-r) at 50% 50%)" }}
      initial={false}
      animate={{ opacity: faded ? 0 : 1 }}
      transition={{ duration: 0.2 }}
    >
      {item.poster?.src ? (
        <img
          src={item.poster.src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={focalPointStyle(item.poster.focal_point)}
        />
      ) : (
        // No poster (some articles): the hole shows Manila — a blank plate.
        <div className="bg-surface-grouped absolute inset-0" />
      )}
    </motion.div>
  )
}

/* ------------------------------------------------------------------- stage */

/* The full title, two-tone: the display head at full Ink, the platform
 * tail (": An ADHD Success Story | Jessica McCabe…") at Ink/55 — present
 * but receded. Stage surfaces only; the jacket face shows the head alone. */
function StageTitle({ title }: { title: string }) {
  const { head, tail } = splitTitle(title)
  return (
    <>
      {head}
      {tail && (
        <span style={{ color: "rgb(var(--batyr-ink-rgb) / 0.55)" }}>
          {tail}
        </span>
      )}
    </>
  )
}

/**
 * The framed stage (v4 §7): every kind opens onto the same inset stage
 * grammar — panels on the Oat ground, ground visible on all four sides.
 * Audio, article and photo stage on elevated paper; video stages in a
 * THEATER — a black surround that continues the media's own letterbox
 * bars, with the caption on a separate paper label beneath it (gapped
 * like the gatefold's notes ⇄ galley split), so leftover stage space
 * reads as cinema dark, never a mis-fitting white mat.
 * The insets clear the dock buttons (≤38px), TopChrome (bottom edge
 * 48px) and the 14px peek at every size (§9.4); at "upnext" the bottom
 * inset grows on the paper curve to reserve the ground band SleeveUpNext
 * lands on — zero intersection by construction — and the media-box follows
 * via use-media-travel's observer. Reduced motion: motion-reduce kills the
 * inset transition (instant cut).
 *
 * Enter/exit: travel kinds are opacity-only 200ms — the travel layout
 * effect must measure the FINAL media rect pre-paint, and a transform
 * entrance would pollute getBoundingClientRect (reduced motion: the fade IS
 * the fallback). article/audio enter on EASE_PAPER from the right as the
 * sleeve exits left (reduced motion: 200ms crossfade).
 */
function SleeveStage({
  ref,
  playlist,
  entry,
  cover,
  index,
  total,
  phase,
  reduce,
  travels,
  audioPlaying,
  stageMediaRef,
  onSettled,
}: {
  ref: Ref<HTMLDivElement>
  playlist: Playlist
  entry: JacketEntry
  cover: Cover
  index: number
  total: number
  phase: JacketPhase
  reduce: boolean
  travels: boolean
  audioPlaying: boolean
  stageMediaRef: Ref<HTMLDivElement>
  onSettled: () => void
}) {
  const { item } = entry
  return (
    <motion.div
      ref={ref}
      data-component="stage"
      data-phase={phase}
      className="absolute top-14 right-3 bottom-[30px] left-11 transition-[bottom] duration-[380ms] ease-[var(--ease-paper)] data-[phase=upnext]:bottom-[164px] motion-reduce:transition-none md:right-6 md:bottom-6 md:left-14 md:data-[phase=upnext]:bottom-[164px]"
      // The reading stage resets the elevated paper to true Paper White — the
      // sleeve carries the cover's colourway, but the opened media (article
      // reader, audio well, photo print, caption label) reads on neutral paper.
      style={{ "--color-surface-elevated": "#ffffff" } as CSSProperties}
      initial={travels || reduce ? { opacity: 0 } : { x: 48, opacity: 0 }}
      animate={
        travels || reduce
          ? { opacity: 1, transition: REDUCED_FADE }
          : { x: 0, opacity: 1, transition: PAPER }
      }
      exit={
        travels || reduce
          ? { opacity: 0, transition: REDUCED_FADE }
          : {
              x: 48,
              opacity: 0,
              transition: { duration: 0.28, ease: EASE_PAPER },
            }
      }
      onAnimationComplete={(definition) => {
        // The enter settling is the no-travel kinds' landing beat (a bridged
        // unmute would fire here); travel kinds land via the travel itself.
        if (
          !travels &&
          typeof definition === "object" &&
          definition !== null &&
          (definition as { opacity?: number }).opacity === 1
        )
          onSettled()
      }}
    >
      {travels ? (
        item.kind === "video" ? (
          /* THE THEATER: video's surround is the media's own black — the
             stage space around the landed record IS the letterbox,
             continued. True black, not Ink: Ink is navy and would seam
             against the embed's true-black bars. No grain, no paper — the
             theater is media, not chrome. The caption gets its own paper
             label below, separated by the gatefold's gap. */
          <div className="flex h-full flex-col gap-3 md:gap-4">
            <div
              data-component="stage-theater"
              className="relative min-h-0 flex-1 rounded-xs bg-black shadow-xs"
            >
              {/* The platter: use-media-travel fits the largest aspect-true
                  rect inside this area and lands the media-box on it. The
                  4px inset keeps the box's square corners inside the
                  theater's radius; being black-on-black, it never reads. */}
              <div
                ref={stageMediaRef}
                data-component="stage-media"
                className="absolute inset-1"
              />
            </div>
            {(item.title || item.byline) && (
              <div
                data-component="stage-label"
                className="bg-surface-elevated flex shrink-0 items-baseline gap-3 rounded-xs px-4 py-2.5 shadow-xs"
              >
                <CatalogMark index={index} total={total} kind={item.kind} />
                <span
                  className="min-w-0 flex-1 truncate"
                  style={{
                    fontFamily: SERIF,
                    fontWeight: 520,
                    fontSize: 15,
                    color: "var(--color-batyr-ink)",
                  }}
                >
                  <StageTitle title={item.title} />
                </span>
                <BylineBlock
                  playlist={playlist}
                  mediaCreator={item.byline}
                  className="hidden max-w-[40%] md:flex"
                />
              </div>
            )}
          </div>
        ) : (
          /* Photo stages as a print on paper — the white mat is the point
             here, so the panel keeps the caption in its own margin. */
          <div className="bg-surface-elevated relative flex h-full flex-col overflow-hidden rounded-xs p-3 shadow-xs md:p-4">
            <GrainLayer />
            <div
              ref={stageMediaRef}
              data-component="stage-media"
              className="min-h-0 w-full flex-1"
            />
            <div className="flex shrink-0 items-baseline gap-3 pt-3">
              <CatalogMark index={index} total={total} kind={item.kind} />
              <span
                className="min-w-0 flex-1 truncate"
                style={{
                  fontFamily: SERIF,
                  fontWeight: 520,
                  fontSize: 15,
                  color: "var(--color-batyr-ink)",
                }}
              >
                <StageTitle title={item.title} />
              </span>
              <BylineBlock
                playlist={playlist}
                mediaCreator={item.byline}
                className="hidden max-w-[40%] md:flex"
              />
            </div>
          </div>
        )
      ) : item.kind === "audio" ? (
        /* The record on the stage: the vinyl render rides birds-eye behind the
           content, spinning while the embed plays; the well is that record's
           card, seated on an asymmetric stage inside the frame, byline and all.
           Never a centered hero card. */
        <>
          <SpinningRecord
            playing={audioPlaying}
            reduce={reduce}
            label={{ base: cover.base, ink: cover.ink, glow: cover.glow }}
            catalog={catalogMark(index, total)}
          />
          <div className="bg-surface-elevated @container absolute bottom-0 left-0 flex w-full max-w-[440px] flex-col gap-3 rounded-xs p-4 shadow-xs md:bottom-4 md:left-4">
            <div className="flex items-baseline justify-between gap-3">
              <CatalogMark index={index} total={total} kind={item.kind} />
              <span style={marginalia}>press play on the record</span>
            </div>
            <h2
              style={{
                fontFamily: SERIF,
                fontWeight: 540,
                fontSize: 20,
                lineHeight: 1.2,
                color: "var(--color-batyr-ink)",
              }}
            >
              <StageTitle title={item.title} />
            </h2>
            <HairlineRule />
            <BylineBlock playlist={playlist} mediaCreator={item.byline} />
            <AudioWell item={item} height={352} />
          </div>
        </>
      ) : (
        /* The gatefold spread (§7.4) — the stage grammar's model: on desktop
           the spread's left panel reprints the jacket face beside a 65ch
           galley; on the phone a slim header band sits above the reader. */
        <div className="flex h-full flex-col gap-3 md:flex-row md:gap-4">
          <div className="bg-surface-elevated flex shrink-0 items-center gap-3 rounded-xs px-4 py-2 shadow-xs md:hidden">
            {/* The title now lives in the reader's own masthead, so this phone
                band is just the catalog mark — no title reprint to duplicate. */}
            <CatalogMark index={index} total={total} kind={item.kind} />
          </div>
          <div className="bg-surface-elevated @container relative hidden w-[min(380px,32vw)] shrink-0 flex-col overflow-hidden rounded-xs p-5 shadow-xs md:flex">
            <GrainLayer />
            <CatalogMark
              index={index}
              total={total}
              kind={item.kind}
              style={kicker}
            />
            {/* Note column: the title moved to the reader, so this slim panel is
                just the curator's note, with the byline pinned directly beneath
                it — the byline attributes whoever left the note. */}
            <HairlineRule className="mt-3 mb-3" />
            {item.notes && (
              <JacketNotes
                notes={item.notes}
                reduce={reduce}
                startIndex={0}
                emphasis="stroke"
              />
            )}
            <BylineBlock
              playlist={playlist}
              mediaCreator={item.byline}
              className="mt-3"
            />
          </div>
          <div className="bg-surface-elevated min-h-0 flex-1 overflow-hidden rounded-xs shadow-xs">
            <ArticleReader item={item} turnAccent="ink" />
          </div>
        </div>
      )}
    </motion.div>
  )
}

/* ------------------------------------------------------------ sleeve card */

function SleeveCard({
  playlist,
  entry,
  cover,
  index,
  total,
  phase,
  reduce,
  onOpen,
  onComposed,
}: {
  playlist: Playlist
  entry: JacketEntry
  cover: Cover
  index: number
  total: number
  phase: JacketPhase
  reduce: boolean
  onOpen: () => void
  onComposed: () => void
}) {
  const { item } = entry

  return (
    <motion.div
      data-component="sleeve-card"
      // The card box IS the --card-* rect (no translate: motion's x rides
      // `transform`, left/top stay untouched, and card.y measures true) —
      // a FIXED SQUARE now (h = --card-h = --card-w); the content-hug
      // measurement hook is gone. The type ramp rides the card —
      // per-breakpoint vars the voices in type-voices.ts consume; the
      // gatefold reprint and PaperOutro sit outside the card and keep the
      // fallback ramp.
      className="@container absolute top-[var(--card-y)] left-[var(--card-x)] flex h-[var(--card-h)] w-[var(--card-w)] flex-col rounded-none [--notes-fs:15.5px] [--title-fs:clamp(26px,7.5cqi,34px)] md:[--notes-fs:15px] md:[--title-fs:clamp(38px,6.8cqi_+_9px,64px)]"
      style={{ boxShadow: REST_SHADOW }}
      // ENTER is a crossfade in BOTH modes — the empty sleeve returns composed
      // as one piece (Beat 1), no left-slide. (AnimatePresence initial={false}
      // skips the first page-load enter, so this only governs the close
      // re-mount.) EXIT keeps the x:"-112%" slide — the open "record drawn out
      // sideways" read is unchanged.
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: PAPER }}
      exit={
        reduce
          ? { opacity: 0, transition: REDUCED_FADE } // Reduced motion: 200ms crossfade out, no travel
          : {
              x: "-112%",
              boxShadow: FLIGHT_SHADOW,
              transition: { duration: 0.38, ease: EASE_PAPER },
            }
      }
      onAnimationComplete={(definition) => {
        // Beat 1 done: the empty sleeve has crossfaded in. Hand off to Beat 2
        // (the record slides back in) — or straight to ambient for non-travel
        // kinds (sleeveComposed decides). Mirrors SleeveStage's completion
        // gate; guarded to the closing crossfade so ambient/seating re-renders
        // and the exit slide never fire it.
        if (
          phase === "closing" &&
          typeof definition === "object" &&
          definition !== null &&
          (definition as { opacity?: number }).opacity === 1
        )
          onComposed()
      }}
    >
      {/* The printed sleeve face (designer handoff — "Vinyl Covers"): the
          seeded generative cover, grain, the opening-edge spine shadow, and
          the floating liner credit. Paper is opaque, so the jacket occludes
          the seated half of the record by paint order alone; the crescent
          past the right edge is all that shows. */}
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-none">
        <PatternField
          cover={cover}
          seed={seedFor(item)}
          intensity={COVER_INTENSITY}
        />
        <GrainLayer />
        <SpineShadow />
        <LinerCard
          playlist={playlist}
          item={item}
          index={index}
          total={total}
          reduce={reduce}
        />
      </div>

      {/* The threshold: the whole face is the affordance. */}
      <button
        type="button"
        data-component="sleeve-threshold"
        onClick={onOpen}
        aria-label={`Open — ${item.title}`}
        className={cn(
          "absolute inset-0 z-10 cursor-pointer rounded-none",
          FOCUS_RING,
        )}
      />
    </motion.div>
  )
}

/* -------------------------------------------------------------- spine dock */

function SpineDock({
  entry,
  index,
  total,
  next,
  reduce,
  onReturn,
  onAdvance,
}: {
  entry: JacketEntry
  index: number
  total: number
  next?: JacketEntry
  reduce: boolean
  onReturn: () => void
  onAdvance: () => void
}) {
  const { item } = entry
  const half = Math.ceil(total / 2)
  const side = index < half ? "a" : "b"
  const number = index < half ? index + 1 : index - half + 1

  return (
    <motion.div
      className="absolute inset-y-0 left-0 z-20"
      initial={reduce ? { opacity: 0 } : { x: -36, opacity: 0 }}
      animate={
        reduce
          ? { opacity: 1, transition: REDUCED_FADE } // Reduced motion: spine fades in
          : {
              x: 0,
              opacity: 1,
              transition: { duration: 0.32, ease: EASE_PAPER, delay: 0.22 },
            }
      }
      exit={
        reduce
          ? { opacity: 0, transition: REDUCED_FADE } // Reduced motion: fade out
          : {
              x: -36,
              opacity: 0,
              transition: { duration: 0.28, ease: EASE_PAPER },
            }
      }
    >
      {/* Crate riffle — the next sleeve's spine edge behind the parked
          spine; tap (or a short drag, which Embla owns here) riffles on.
          Plain navigation: shell → Embla, never emitEnded. */}
      {next && (
        <button
          type="button"
          data-component="dock-riffle"
          onClick={onAdvance}
          aria-label={`Next in the crate — ${next.item.title}`}
          className={cn(
            "bg-surface-grouped absolute top-8 bottom-10 left-2.5 z-10 w-7 cursor-pointer rounded-xs shadow-xs",
            FOCUS_RING,
          )}
        >
          <span
            className="text-batyr-ink/40 absolute inset-x-0 top-3 flex justify-center"
            style={{
              ...chromeLabel,
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
            }}
          >
            next
          </span>
        </button>
      )}

      {/* The parked spine — the sleeve compressed against the left edge;
          tappable, returns to Ambient. Bottom inset clears the 14px peek. */}
      <button
        type="button"
        data-component="dock-return"
        onClick={onReturn}
        aria-label={`Back to the sleeve — ${item.title}`}
        className={cn(
          "bg-surface-elevated absolute top-3 bottom-[18px] left-0 z-20 flex w-7 cursor-pointer items-start justify-center rounded-xs pt-3 shadow-xs",
          FOCUS_RING,
        )}
      >
        <span
          className="text-batyr-ink/70 max-h-full overflow-hidden"
          style={{
            ...chromeLabel,
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
          }}
        >
          {side}
          {number} · {splitTitle(item.title).head}
        </span>
      </button>
    </motion.div>
  )
}

/* ---------------------------------------------------------------- up next */

function SleeveUpNext({
  next,
  nextIndex,
  total,
  reduce,
  onAdvance,
  onStay,
}: {
  next: JacketEntry
  nextIndex: number
  total: number
  reduce: boolean
  onAdvance: () => void
  onStay: () => void
}) {
  return (
    <motion.div
      data-component="up-next"
      // The ground band the stage's raised bottom inset reserves (§7):
      // same horizontal insets as the stage, bare Oat by construction.
      className="absolute right-3 bottom-[22px] left-11 z-30 md:right-auto md:bottom-6 md:left-14 md:w-[min(440px,var(--card-w))]"
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
      animate={
        reduce
          ? { opacity: 1, transition: REDUCED_FADE } // Reduced motion: 200ms crossfade
          : { opacity: 1, y: 0, transition: PAPER }
      }
      exit={
        reduce
          ? { opacity: 0, transition: REDUCED_FADE } // Reduced motion: 200ms crossfade
          : { opacity: 0, y: 16, transition: PAPER }
      }
    >
      <div className="bg-surface-elevated overflow-hidden rounded-xs shadow-xs">
        <button
          type="button"
          onClick={onAdvance}
          aria-label={`Turn the page — ${next.item.title}`}
          className={cn(
            "flex w-full cursor-pointer items-center gap-3 p-3 text-left",
            FOCUS_RING,
          )}
        >
          {next.item.poster?.src ? (
            // The next jacket previews as an uncorrected proof.
            <img
              src={next.item.poster.src}
              alt=""
              className="size-12 shrink-0 rounded-xs object-cover"
              style={{ filter: DUOTONE_FILTER }}
            />
          ) : (
            <div className="bg-surface-grouped size-12 shrink-0 rounded-xs" />
          )}
          <div className="min-w-0 flex-1">
            <CatalogMark
              index={nextIndex}
              total={total}
              kind={next.item.kind}
            />
            <p
              className="truncate"
              style={{
                fontFamily: SERIF,
                fontWeight: 520,
                fontSize: 15,
                color: "var(--color-batyr-ink)",
              }}
            >
              {splitTitle(next.item.title).head}
            </p>
            {next.item.notes && (
              <p
                className="truncate"
                style={{ ...linerNotes, fontSize: 13, maxWidth: "none" }}
              >
                {firstSentence(next.item.notes)}
              </p>
            )}
          </div>
        </button>
        <div className="px-3 pb-2">
          <UpNextCaption onStay={onStay} />
        </div>
        <UpNextRule onComplete={onAdvance} />
      </div>
    </motion.div>
  )
}

/* -------------------------------------------------------------- crate rail */

/**
 * ProgressRail reskin (brief-gap fill): crate ticks on the spine axis —
 * flat Ink dashes, the current one widened. Hidden while a sleeve is open;
 * the parked spine and riffle carry the position then. Hidden below `sm`
 * too — the narrow viewport crowds it against the jacket, so the peek
 * carets carry position there instead.
 */
function CrateRail({
  total,
  selectedIndex,
  hidden,
}: {
  total: number
  selectedIndex: number
  hidden: boolean
}) {
  if (hidden) return null
  return (
    <div className="pointer-events-none absolute top-1/2 left-1.5 z-10 hidden -translate-y-1/2 flex-col items-start gap-1.5 sm:flex">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={cn(
            "h-0.5 transition-all motion-reduce:transition-none",
            i === selectedIndex
              ? "bg-batyr-ink w-5"
              : i < selectedIndex
                ? "bg-batyr-ink/40 w-3"
                : "bg-batyr-ink/25 w-3",
          )}
        />
      ))}
    </div>
  )
}
